export type CampaignType = "email" | "sms";

export interface CampaignFilters {
  includeZipCodes: string[] | null;
  excludeZipCodes: string[] | null;
  includeCities: string[] | null;
  excludeCities: string[] | null;
  includeStates: string[] | null;
  excludeStates: string[] | null;
  includeTags: number[] | null;
  excludeTags: number[] | null;
  includePropertyTags: number[] | null;
  excludePropertyTags: number[] | null;
  activeStatus: "active" | "inactive" | "all" | null;
  servicePlanIds: number[] | null;
  excludeServicePlanIds: number[] | null;
  servicePlanExpiringDays: number | null;
  lastApptBefore: string | null;
  lastApptAfter: string | null;
  outstandingBalance: boolean | null;
  unrespondedEstimates: boolean | null;
  unrespondedEstimatesDays: number | null;
  includeCustomerIds: number[] | null;
  excludeCustomerIds: number[] | null;
  manualEmails: string[] | null;
  manualPhones: string[] | null;
  appointmentTypeIds: number[] | null;
  excludeAppointmentTypeIds: number[] | null;
}

export interface SelectedCustomer {
  id: number;
  name: string;
  email: string;
  companyName?: string;
}

export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "failed"
  | "cancelled";

export interface ICampaign {
  id: string;
  businessId: string;
  campaignType: CampaignType;
  templateId: string | null;
  name: string;
  subject: string | null;
  body: string | null;
  designJson: Record<string, unknown> | null;
  messageText: string | null;
  status: CampaignStatus;
  sendDate: string | null;
  sentAt: string | null;
  sentBy: string | null;
  sentByName?: string | null;
  /** Pending schedule (when status is "scheduled") — populated by getCampaign. */
  schedule?: { scheduledAt: string; timezone: string } | null;
  totalRecipients: number;
  totalSent: number;
  totalFailed: number;
  filters: CampaignFilters;
  createdAt: string;
  updatedAt: string;
}

export interface LocationCombo {
  state: string;
  city: string;
  zipcode: string;
}

export interface FilterOptions {
  servicePlans: { id: number; name: string }[];
  customerTags: { id: number; name: string; color?: string | null }[];
  propertyTags: { id: number; name: string; color?: string | null }[];
  zipCodes: string[];
  cities: string[];
  states: string[];
  locationCombos: LocationCombo[];
  appointmentTypes: { id: number; name: string }[];
}

export interface Recipient {
  bcustomerId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string;
  companyName: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  /** Set (opt-out timestamp) when the customer unsubscribed from this campaign's channel — preview only. */
  optedOutAt?: string | null;
  /** Preview only: the address is blocked (bounce/complaint/unsubscribe) and the send skips it. */
  suppression?: { reason: string } | null;
  /** Lookup only: whether the typed value is this customer's primary address or an extra contact. */
  matchedOn?: "primary" | "extra";
  /** Lookup only: consent of the TYPED value for this person (their opt-out + the address's suppression). */
  consent?: RecipientConsentState;
}

/** Consent of one address/number: the person's channel opt-out plus the address-level block. */
export interface RecipientConsentState {
  optedOut: boolean;
  optedOutAt: string | null;
  suppressed: { reason: string; source: string | null } | null;
}

export interface RecipientCountResponse {
  /** How many will actually be sent to. Unchanged meaning (ESERV2-1467). */
  count: number;
  /**
   * Why anyone matching the filters was left out. Mutually exclusive buckets,
   * so they add up. Null when the breakdown could not be computed — the count
   * is still valid in that case.
   */
  skipped?: {
    noAddress: number;
    optedOut: number;
    suppressed: number;
    total: number;
  } | null;
}

export interface CampaignReportEntry {
  recipientAddress: string;
  channel: CampaignType;
  status: string;
  sentAt: string | null;
  errorMessage: string | null;
  openedAt: string | null;
  openCount: number;
  firstClickedAt: string | null;
  clickCount: number;
}

export interface TrackingSummary {
  uniqueOpens: number;
  totalOpens: number;
  uniqueClicks: number;
  totalClicks: number;
}

/**
 * Delivery counts for a campaign, counted from the send log — the same rows the
 * delivery table lists.
 *
 * The campaign's own totalSent/totalFailed are written when the send finishes
 * and never revised, so a later delivery result (a Twilio failure callback, say)
 * leaves them contradicting the rows. These are read fresh with the report.
 */
export interface CampaignSendSummary {
  total: number;
  sent: number;
  failed: number;
  queued: number;
  /** Delivery was refused. Never delivered, so it is not counted as sent. */
  bounced: number;
  /** Delivered and read, then reported as spam by the recipient. */
  complained: number;
}

export type ReportStatusFilter =
  | "all"
  | "sent"
  | "failed"
  | "bounced"
  | "complained"
  | "opened"
  | "not_opened";

/** Dropdown counts, computed by the API in one pass over the send log. */
export type ReportCounts = Record<ReportStatusFilter, number>;

/** Header figures + counts; no rows (ESERV2-1457 slice 3.6b). */
export interface CampaignReportSummary {
  campaign: ICampaign;
  tracking: TrackingSummary;
  summary: CampaignSendSummary;
  unsubscribed: number;
  counts: ReportCounts;
}

/** One page of the delivery log. */
export interface CampaignReportEntriesPage {
  rows: CampaignReportEntry[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

export interface CampaignReport {
  campaign: ICampaign;
  entries: CampaignReportEntry[];
  tracking: TrackingSummary;
  summary: CampaignSendSummary;
  /**
   * People this campaign made unsubscribe, attributed to the last campaign
   * sent to them at or before they opted out. Deliberately outside `summary`:
   * a recipient can be counted as sent AND have unsubscribed afterwards, so it
   * is not another slice of the delivery buckets.
   */
  unsubscribed: number;
}

export const DEFAULT_FILTERS: CampaignFilters = {
  includeZipCodes: null,
  excludeZipCodes: null,
  includeCities: null,
  excludeCities: null,
  includeStates: null,
  excludeStates: null,
  includeTags: null,
  excludeTags: null,
  includePropertyTags: null,
  excludePropertyTags: null,
  activeStatus: null,
  servicePlanIds: null,
  excludeServicePlanIds: null,
  servicePlanExpiringDays: null,
  lastApptBefore: null,
  lastApptAfter: null,
  outstandingBalance: null,
  unrespondedEstimates: null,
  unrespondedEstimatesDays: null,
  includeCustomerIds: null,
  excludeCustomerIds: null,
  manualEmails: null,
  manualPhones: null,
  appointmentTypeIds: null,
  excludeAppointmentTypeIds: null,
};
